// Google OAuth Sign-In Module
// Implements OAuth 2.0 authorization code flow with PKCE

// OAuth Configuration
const GOOGLE_OAUTH_CONFIG = {
  clientIdE:
    "311328107313-ms6qa45rgrs3rkkegq04gloil4l5pm9m.apps.googleusercontent.com",
  clientIdI:
    "513712273819-rdqgae7sg4bhp78ejmdrjsrp4sj967qc.apps.googleusercontent.com",
  redirectUri: window.location.origin + "/oauth-google-callback.html",
  scope: "openid email profile",
  responseType: "code",
  authorizationEndpoint: "https://accounts.google.com/o/oauth2/v2/auth",
};

// Session storage keys
const STORAGE_KEYS = {
  STATE: "oauth_state",
  CODE_VERIFIER: "oauth_code_verifier",
  INITIATED_AT: "oauth_initiated_at",
  VISA_CATEGORY: "visa_category",
  COUNTRY: "country",
};

// OAuth state expiration time (10 minutes)
const STATE_EXPIRATION_MS = 10 * 60 * 1000;

/**
 * Generate a cryptographically secure random string
 * @param {number} length - Length of the string to generate
 * @returns {string} Base64URL-encoded random string
 */
function generateRandomString(length) {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return base64URLEncode(array);
}

/**
 * Base64URL encode a byte array
 * @param {Uint8Array} buffer - Byte array to encode
 * @returns {string} Base64URL-encoded string
 */
function base64URLEncode(buffer) {
  const base64 = btoa(String.fromCharCode(...buffer));
  return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
}

/**
 * Generate PKCE code challenge from code verifier using SHA-256
 * @param {string} codeVerifier - The code verifier string
 * @returns {Promise<string>} Base64URL-encoded SHA-256 hash
 */
async function generateCodeChallenge(codeVerifier) {
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const hash = await crypto.subtle.digest("SHA-256", data);
  return base64URLEncode(new Uint8Array(hash));
}

/**
 * Store OAuth state and code verifier in session storage
 * @param {string} state - CSRF protection state parameter
 * @param {string} codeVerifier - PKCE code verifier
 * @param {string} visaCategory - Selected visa category
 * @param {string} country - Selected country
 */
function storeOAuthState(state, codeVerifier, visaCategory, country) {
  sessionStorage.setItem(STORAGE_KEYS.STATE, state);
  sessionStorage.setItem(STORAGE_KEYS.CODE_VERIFIER, codeVerifier);
  sessionStorage.setItem(STORAGE_KEYS.INITIATED_AT, Date.now().toString());
  sessionStorage.setItem(STORAGE_KEYS.VISA_CATEGORY, visaCategory);
  sessionStorage.setItem(STORAGE_KEYS.COUNTRY, country);
}

/**
 * Retrieve OAuth state from session storage
 * @returns {Object|null} Object with state, codeVerifier, visaCategory, and country, or null if expired/missing
 */
function retrieveOAuthState() {
  const state = sessionStorage.getItem(STORAGE_KEYS.STATE);
  const codeVerifier = sessionStorage.getItem(STORAGE_KEYS.CODE_VERIFIER);
  const initiatedAt = sessionStorage.getItem(STORAGE_KEYS.INITIATED_AT);
  const visaCategory = sessionStorage.getItem(STORAGE_KEYS.VISA_CATEGORY);
  const country = sessionStorage.getItem(STORAGE_KEYS.COUNTRY);

  if (!state || !codeVerifier || !initiatedAt) {
    return null;
  }

  // Check if state has expired
  const elapsed = Date.now() - parseInt(initiatedAt, 10);
  if (elapsed > STATE_EXPIRATION_MS) {
    clearOAuthState();
    return null;
  }

  return { state, codeVerifier, visaCategory, country };
}

/**
 * Clear OAuth state from session storage
 */
function clearOAuthState() {
  sessionStorage.removeItem(STORAGE_KEYS.STATE);
  sessionStorage.removeItem(STORAGE_KEYS.CODE_VERIFIER);
  sessionStorage.removeItem(STORAGE_KEYS.INITIATED_AT);
  sessionStorage.removeItem(STORAGE_KEYS.VISA_CATEGORY);
  sessionStorage.removeItem(STORAGE_KEYS.COUNTRY);
}

/**
 * Construct Google OAuth authorization URL
 * @param {string} state - CSRF protection state parameter
 * @param {string} codeChallenge - PKCE code challenge
 * @returns {string} Complete authorization URL
 */
function buildAuthorizationUrl(state, codeChallenge) {
  var clientId = GOOGLE_OAUTH_CONFIG.clientIdI;
  if (GOOGLE_OAUTH_CONFIG.redirectUri.includes("www.usvisabulletinhub.com")) {
    clientId = GOOGLE_OAUTH_CONFIG.clientIdE;
  }
  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: GOOGLE_OAUTH_CONFIG.redirectUri,
    response_type: GOOGLE_OAUTH_CONFIG.responseType,
    scope: GOOGLE_OAUTH_CONFIG.scope,
    state: state,
    code_challenge: codeChallenge,
    code_challenge_method: "S256",
  });

  return `${GOOGLE_OAUTH_CONFIG.authorizationEndpoint}?${params.toString()}`;
}

/**
 * Initiate Google OAuth sign-in flow
 * Generates state and code_verifier, stores them, and redirects to Google
 * @param {string} visaCategory - Selected visa category
 * @param {string} country - Selected country
 */
async function initiateGoogleSignIn(visaCategory, country) {
  try {
    // Validate required parameters
    if (!visaCategory || !country) {
      throw new Error("Visa category and country are required");
    }

    // Generate state parameter (32 bytes = 256 bits of entropy)
    const state = generateRandomString(32);

    // Generate PKCE code_verifier (32 bytes = 256 bits of entropy)
    const codeVerifier = generateRandomString(32);

    // Generate code_challenge from code_verifier
    const codeChallenge = await generateCodeChallenge(codeVerifier);

    // Store state, code_verifier, visa_category, and country in session storage
    storeOAuthState(state, codeVerifier, visaCategory, country);

    // Build authorization URL
    const authUrl = buildAuthorizationUrl(state, codeChallenge);

    // Redirect to Google OAuth consent screen
    window.location.href = authUrl;
  } catch (error) {
    console.error("Failed to initiate Google sign-in:", error);
    throw new Error("Failed to initiate sign-in. Please try again.");
  }
}

// Export functions for use in other modules (ES module syntax)
export {
  generateRandomString,
  generateCodeChallenge,
  initiateGoogleSignIn,
  retrieveOAuthState,
  clearOAuthState,
  GOOGLE_OAUTH_CONFIG,
};
